import React from 'react';
import { Tooltip } from "antd";
import { useTranslation } from "react-i18next";

interface TradingStatisticsProps {
    report: any;
    liveTrade?: any;
}

const TradingStatistics: React.FC<TradingStatisticsProps> = ({ report }) => {
    const { t } = useTranslation();

    const netProfitVal = Number(report?.performance_summary?.net_profit ?? report?.sum_net_profit ?? 0);
    const countDays = report?.count_trading_days ?? 0;
    const avgProfitPerDayVal = countDays > 0 ? netProfitVal / countDays : 0;
    const avgProfitPerTradeVal = Number(
        report?.performance_summary?.avg_profit_per_trade ??
        (report?.total_trades > 0 ? netProfitVal / report.total_trades : 0)
    );
    const avgLossVal = Math.abs(Number(report?.mean_loss ?? report?.performance_summary?.avg_loss_per_loss ?? 0));

    const stats = [
        {
            label: t('Balance', 'Số dư tài khoản'),
            tooltip: t('tooltip_today_balance'),
            value: `$${Number(report?.today_balance ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            colorClass: 'text-[#17233c]',
        },
        {
            label: t('TotalTrades', 'Tổng giao dịch'),
            tooltip: t('tooltip_total_trades'),
            value: `${report?.total_trades ?? 0}`,
            colorClass: 'text-[#17233c]',
        },
        {
            label: t('winrate', 'Tỷ lệ thắng'),
            tooltip: t('tooltip_winrate'),
            value: `${((report?.win_rate ?? 0) * 100).toFixed(1)}%`,
            colorClass: 'text-[#14b8a6]',
        },
        {
            label: t('NetProfit', 'Lợi nhuận ròng'),
            tooltip: t('tooltip_net_profit'),
            value: netProfitVal < 0
                ? `-$${Math.abs(netProfitVal).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                : `$${netProfitVal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            colorClass: netProfitVal < 0 ? 'text-[#ef4444]' : 'text-[#14b8a6]',
        },
        {
            label: t('GrossProfit', 'Tổng lợi nhuận'),
            tooltip: t('tooltip_gross_profit'),
            value: `$${Number(report?.performance_summary?.gross_profit ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            colorClass: 'text-[#14b8a6]',
        },
        {
            label: t('TotalFees', 'Tổng phí'),
            tooltip: t('tooltip_total_fees'),
            value: `$${Number(report?.performance_summary?.total_fees ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            colorClass: 'text-[#17233c]',
        },
        {
            label: t('ProfitableTrades', 'Giao dịch có lợi nhuận'),
            tooltip: t('tooltip_profitable_trades'),
            value: `${report?.performance_summary?.profitable_trades ?? 0}`,
            colorClass: 'text-[#14b8a6]',
        },
        {
            label: t('LosingTrades', 'Giao dịch thua lỗ'),
            tooltip: t('tooltip_losing_trades'),
            value: `${report?.performance_summary?.losing_trades ?? 0}`,
            colorClass: 'text-[#ef4444]',
        },
        {
            label: t('trading_days', 'Số ngày giao dịch'),
            tooltip: t('tooltip_count_trading_days'),
            value: `${report?.count_trading_days ?? 0}`,
            colorClass: 'text-[#17233c]',
        },
        {
            label: t('AvgProfitPerDay', 'LN trung bình / ngày'),
            tooltip: t('tooltip_avg_profit_per_day', 'Lợi nhuận trung bình mỗi ngày giao dịch'),
            value: avgProfitPerDayVal < 0
                ? `-$${Math.abs(avgProfitPerDayVal).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                : `$${avgProfitPerDayVal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            colorClass: avgProfitPerDayVal < 0 ? 'text-[#ef4444]' : 'text-[#14b8a6]',
        },
        {
            label: t('AvgProfitPerTrade', 'LN trung bình / lệnh'),
            tooltip: t('tooltip_avg_profit_per_trade'),
            value: avgProfitPerTradeVal < 0
                ? `-$${Math.abs(avgProfitPerTradeVal).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                : `$${avgProfitPerTradeVal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            colorClass: avgProfitPerTradeVal < 0 ? 'text-[#ef4444]' : 'text-[#14b8a6]',
        },
        {
            label: t('AvgLoss', 'Mức lỗ trung bình'),
            tooltip: t('tooltip_mean_loss'),
            value: `$${avgLossVal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            colorClass: 'text-[#17233c]',
        },
    ];

    return (
        <div className="bg-white rounded-[10px] border border-[#e5e9f0] p-[18px_20px] shadow-[0_1px_2px_rgba(16,24,40,0.03)] mt-6">
            <h2 className="!text-[13.5px] !font-semibold !text-[#0a1a33] mb-[14px] flex items-center gap-1.5 m-0">
                <Tooltip title={t('tooltip_TradingStatistics', 'Thống kê chi tiết các chỉ số giao dịch')}>
                    <span>{t('TradingStatisticsOverview', 'Thống kê giao dịch tổng hợp')}</span>
                </Tooltip>
            </h2>

            <div className="grid grid-cols-2 md:grid-cols-4">
                {stats.map((stat, idx) => (
                    <div
                        key={idx}
                        className={`p-[14px_16px] border-b border-[#f1f3f7] ${
                            idx % 2 === 0 ? 'border-r md:border-r' : 'border-r-0 md:border-r'
                        } ${
                            (idx + 1) % 4 === 0 ? 'md:border-r-0' : ''
                        }`}
                    >
                        <div className="text-[11px] text-[#8a94a6] mb-[5px]">
                            {stat.tooltip ? (
                                <Tooltip title={stat.tooltip} className="cursor-help inline-flex items-center gap-1">
                                    <span>{stat.label}</span>
                                </Tooltip>
                            ) : (
                                stat.label
                            )}
                        </div>
                        <div className={`text-[16.5px] font-bold ${stat.colorClass} font-mono-num`}>
                            {stat.value}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default TradingStatistics;